 
 
 
import { Component, Input, input, Output, EventEmitter, signal, effect, computed, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { ControlValueAccessor, FormControl, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { DateRange } from 'src/app/models/lt-daterange.model';

@Component({
  selector: 'app-lt-daterange',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTDateRangeComponent),
      multi: true,
    },
  ],
  template: `
    <mat-form-field [appearance]="fillMode" class="full-width">
      @if(placeholder){
        <mat-label>{{placeholder}}</mat-label>
      }
      <mat-date-range-input
          [formGroup]="range"
          [rangePicker]="rangePicker"
          [min]="minDate()"
          [max]="maxDate()"
          [required]="required()">
        <input matStartDate
              formControlName="start"
              [value]="startDate()"
              [placeholder]="startLabel"
              (dateChange)="updateStartDate($event)"
              (blur)="onTouched()">
        <input matEndDate
              formControlName="end"
              [value]="endDate()"
              [placeholder]="endLabel"
              (dateChange)="updateEndDate($event)"
              (blur)="onTouched()">
      </mat-date-range-input>
      @if(hint){
        <mat-hint>{{hint}}</mat-hint>
      }
      <mat-datepicker-toggle matIconSuffix [for]="rangePicker"></mat-datepicker-toggle>
      <mat-date-range-picker #rangePicker></mat-date-range-picker>

      @if(range.controls['start'].hasError('matStartDateInvalue')) {
        <mat-error>Start date is invalid</mat-error>
      }
      @if(range.controls['end'].hasError('matEndDateInvalue')) {
        <mat-error>Start date is invalid</mat-error>
      }
    </mat-form-field>
  `,
  styles: '',
})
export class LTDateRangeComponent implements ControlValueAccessor {
  //#region Inputs

  @Input() fillMode: 'outline' | 'fill' = 'outline';
  @Input() placeholder = 'Choose a date range';
  @Input() startLabel = 'Start Date';
  @Input() endLabel = 'End Date';
  @Input() hint = null;

  required = input(false);

  //#region Disabled
  @Input() set disabled(isDisabled: boolean) {
    // this.disabled = isDisabled;
    if (isDisabled) {
      this.range.disable();
    } else {
      this.range.enable();
    }
  }
  get disabled(): boolean {
    return this.range.disabled;
  }
  //#endregion Disabled

  //#region MinDate
  private _minDate = signal<Date | null>(null);
  @Input() set min(value: Date | null) {
    this._minDate.set(value);
  }
  get minDate() {
    return this._minDate;
  }
  //#endregion MinDate

  //#region MaxDate
  private _maxDate = signal<Date | null>(null);
  @Input() set max(value: Date | null) {
    this._maxDate.set(value);
  }
  get maxDate() {
    return this._maxDate;
  }
  //#endregion MaxDate

  //#region Format
  private _format = signal('MM/DD/yyyy');
  @Input() set format(value: string) {
    if (value) {
      this._format.set(value);
      this.showFormattedHint = true;
    }
  }
  get format(): string {
    return this._format();
  }
  // Whether to show the formatted date hint
  showFormattedHint = false;
  //#endregion Format

  //#endregion Inputs

  // ouput
  @Output() valueChange = new EventEmitter<DateRange>();

  // signals
  startDate = signal<Date | null>(null);
  endDate = signal<Date | null>(null);

  // Computed value for the complete range
  dateRange = computed<DateRange>(() => {
    return {
      start: this.startDate(),
      end: this.endDate(),
    };
  });

  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor() {
    effect(() => {
      const range = this.dateRange();
      this.onChange(range);
      this.valueChange.emit(range);
    });
  }

  updateStartDate(event: any): void {
    this.startDate.set(event.value);
  }

  updateEndDate(event: any): void {
    this.endDate.set(event.value);
  }

  writeValue(value: DateRange): void {
    if (value) {
      this.startDate.set(value.start);
      this.endDate.set(value.end);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.range.disable();
    } else {
      this.range.enable();
    }
  }

}
